<script setup lang="ts">
import LazyImage from '@/vue/atoms/lazy-image.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {computed, type ComponentPublicInstance, type CSSProperties, ref} from 'vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {useLightbox} from '@/ts/composables/pure/use-lightbox';
import {useLogger} from '@/ts/composables/pure/use-logger';

const props = withDefaults(defineProps<{
  src: string;
  isLightBoxed?: boolean;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  format?: 'auto' | 'jpg' | 'png' | 'tiff' | 'webp';
  imageClass?: string;
  containerClass?: string;
  containerStyle?: CSSProperties;
}>(), {
  isLightBoxed: false,
  loading: 'lazy',
  format: 'webp',
});

const {open} = useLightbox();
const {error: logError} = useLogger();

const imageUrl = computed(() => calculateMediaUrl(props.src));
const imageRef = ref<ComponentPublicInstance | null>(null);

const FALLBACK_WIDTH = 1920;
const FALLBACK_HEIGHT = 1080;

async function getRenderedImageDimensions(): Promise<{width: number; height: number} | null> {
  // Try to get dimensions from the actual rendered image element
  // imageRef points to Vue component instance, so we need $el to access the DOM
  const componentEl = imageRef.value?.$el as HTMLElement | undefined;
  const imgElement = componentEl?.tagName === 'IMG'
    ? componentEl as HTMLImageElement
    : componentEl?.querySelector('img');

  if (!imgElement) {
    return null;
  }

  // If image is already loaded, return dimensions immediately
  if (imgElement.naturalWidth && imgElement.naturalHeight) {
    return {
      width: imgElement.naturalWidth,
      height: imgElement.naturalHeight,
    };
  }

  // Wait for image to load if not ready yet
  return new Promise((resolve) => {
    imgElement.addEventListener('load', () => {
      resolve({
        width: imgElement.naturalWidth || FALLBACK_WIDTH,
        height: imgElement.naturalHeight || FALLBACK_HEIGHT,
      });
    }, {once: true});

    imgElement.addEventListener('error', () => {
      resolve(null);
    }, {once: true});
  });
}

let isOpening = false;

async function handleImageClick(): Promise<void> {
  if (!props.isLightBoxed || isOpening) {
    return;
  }

  isOpening = true;

  try {
    // Get dimensions from the already-rendered image element (waits if still loading)
    const dimensions = await getRenderedImageDimensions() ?? {
      width: FALLBACK_WIDTH,
      height: FALLBACK_HEIGHT,
    };

    await open({
      items: [{
        img: imageUrl.value,
        alt: props.alt ?? '',
        caption: props.title ?? '',
        width: dimensions.width,
        height: dimensions.height,
      }],
      position: 0,
    });
  } catch (err) {
    // The library is fetched on this first click: a failed fetch would otherwise be a silent dead click.
    logError('Failed to open the lightbox', err)();
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isOpening = false;
  }
}
</script>

<template>
  <div
    :class="['lightbox-image-container', containerClass, {'lightbox-enabled': isLightBoxed}]"
    :style="containerStyle"
    :role="isLightBoxed ? 'button' : undefined"
    :tabindex="isLightBoxed ? 0 : undefined"
    @click="handleImageClick"
    @keydown.enter="handleImageClick"
    @keydown.space.prevent="handleImageClick"
  >
    <lazy-image
      ref="imageRef"
      :src="src"
      :alt-txt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      :format="format"
      :class="['lightbox-image', imageClass]"
    />

    <div v-if="isLightBoxed" class="lightbox-indicator" aria-hidden="true">
      <svg-illustration
        name="media-maximize.svg"
        inner-class="lightbox-icon"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lightbox-image-container {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: hidden;

  &.lightbox-enabled {
    cursor: pointer;

    &:hover .lightbox-indicator {
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid var(--Primary-primary-color-2);
      outline-offset: 2px;
      border-radius: var(--border-radius);
    }
  }
}

.lightbox-image {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius);
}

.lightbox-indicator {
  position: absolute;
  top: var(--Spacing-spacing-2);
  right: var(--Spacing-spacing-2);
  width: 32px;
  height: 32px;
  background-color: var(--Neutral-UI-neutral-ui-100);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--Neutral-UI-neutral-ui-0);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  :deep(.lightbox-icon) {
    width: 18px;
    height: 18px;
  }

  @include screen-md {
    width: 40px;
    height: 40px;

    :deep(.lightbox-icon) {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
